import { useState } from "react";
import { api, fmtErr } from "../lib/api";
import { ArrowRight } from "lucide-react";

export const TYPE_LABEL = {
  HUMAN_DIRECT_HIRE: "Human Direct Hire", AI_EMPLOYEE: "AI Employee", AI_DEPARTMENT: "AI Department",
  AI_EXECUTIVE: "AI Executive", AI_SALES_WORKFORCE: "AIA Sales Workforce", MANAGED_HUMAN_TEAM: "Managed Human Team",
  HYBRID_WORKFORCE: "Hybrid Workforce", SALESHUB_PLAN: "SalesHub Plan", FRONTDESK_MEMBERSHIP: "FrontDesk AI Pro Memberships",
};

export const READINESS_BADGE = {
  AVAILABLE: { cls: "bg-teal-50 text-teal-700 border-teal-200", label: "Available" },
  COMING_SOON: { cls: "bg-amber-50 text-amber-800 border-amber-300", label: "Coming Soon" },
  CONTACT_SALES: { cls: "bg-slate-100 text-slate-700 border-slate-300", label: "Contact Sales" },
  CUSTOM_CONFIG: { cls: "bg-indigo-50 text-indigo-700 border-indigo-200", label: "Custom Configuration Required" },
  INACTIVE: { cls: "bg-slate-100 text-slate-500 border-slate-200", label: "Inactive" },
};

const AI_TYPES = new Set(["AI_EMPLOYEE", "AI_DEPARTMENT", "AI_EXECUTIVE"]);

export const SALES_EMAIL = "sales@connectmyhub.com";
const SalesMail = () => <a href={`mailto:${SALES_EMAIL}`} className="underline font-semibold" data-testid="sales-email-link">{SALES_EMAIL}</a>;

export function ctaLabel(s) {
  if (!AI_TYPES.has(s.solution_type)) return s.cta || "LEARN MORE";
  if (s.readiness === "AVAILABLE") return s.solution_type === "AI_DEPARTMENT" ? "HIRE THIS DEPARTMENT" : "HIRE THIS AI EMPLOYEE";
  if (s.readiness === "CUSTOM_CONFIG") return "GET STARTED";
  if (s.readiness === "COMING_SOON") return "JOIN WAITLIST";
  return "BUILD MY AI WORKFORCE";
}

export function priceLine(s) {
  if (s.readiness === "CONTACT_SALES" && !s.monthly_price) return "Contact Sales";
  if (s.readiness === "COMING_SOON" && !s.monthly_price) return "Coming Soon";
  if (s.monthly_price == null) return "—";
  let base = `$${Number(s.monthly_price).toLocaleString()}/mo`;
  if (s.pricing_note) base += ` ${s.pricing_note}`;
  if (s.setup_fee) base += ` · $${Number(s.setup_fee).toLocaleString()} setup`;
  if (s.annual_price) base += ` · $${Number(s.annual_price).toLocaleString()}/yr`;
  return base;
}

export function useWorkforceCta() {
  const [interest, setInterest] = useState(null);
  const [form, setForm] = useState({ business_name: "", contact_name: "", email: "", phone: "", notes: "" });
  const [result, setResult] = useState(null);
  const [checkoutMsg, setCheckoutMsg] = useState(null);
  const [error, setError] = useState("");

  const handleCta = async (s) => {
    setError(""); setCheckoutMsg(null); setResult(null);
    if (s.readiness === "AVAILABLE" || s.readiness === "CUSTOM_CONFIG") {
      try {
        const r = await api.post(`/public/workforce/${s.sku}/checkout-url`);
        window.open(r.data.checkout_url, "_blank", "noopener");
      } catch (err) {
        const d = err?.response?.data?.detail;
        setCheckoutMsg({ sku: s.sku, message: d?.message || "Checkout is currently unavailable. Contact Sales and our team will complete setup." });
      }
      return;
    }
    if (s.readiness === "COMING_SOON") setInterest({ sku: s.sku, kind: "WAITLIST", name: s.name });
    if (s.readiness === "CONTACT_SALES") setInterest({ sku: s.sku, kind: "CONTACT_SALES", name: s.name });
  };

  const submit = async (e) => {
    e.preventDefault();
    try { setResult((await api.post("/public/workforce/interest", { sku: interest.sku, kind: interest.kind, ...form })).data); }
    catch (err) { setError(fmtErr(err?.response?.data?.detail) || "Something went wrong."); }
  };
  const close = () => { setInterest(null); setResult(null); };

  const modal = interest && (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={close}>
      {!result ? (
        <div onClick={e => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-md p-6" data-testid="interest-modal">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-teal-600">{interest.kind === "WAITLIST" ? "Join waitlist · Reserve deployment" : "Build my AI workforce · Contact sales"}</div>
          <div className="font-display font-bold text-lg mt-1 text-slate-900">{interest.name}</div>
          <form onSubmit={submit} className="mt-4 space-y-3">
            <Field label="Business name" v={form.business_name} on={v => setForm({ ...form, business_name: v })} testid="int-biz"/>
            <Field label="Your name" v={form.contact_name} on={v => setForm({ ...form, contact_name: v })} testid="int-name"/>
            <Field label="Work email" v={form.email} on={v => setForm({ ...form, email: v })} testid="int-email" type="email"/>
            <Field label="Phone (optional)" v={form.phone} on={v => setForm({ ...form, phone: v })} testid="int-phone" required={false} type="tel"/>
            <Field label="Notes (optional)" v={form.notes} on={v => setForm({ ...form, notes: v })} testid="int-notes" required={false} textarea/>
            {error && <div className="rounded-md bg-red-50 border border-red-200 text-red-800 text-xs p-2">{error}</div>}
            <p className="text-[11px] text-slate-500">Prefer email? Reach our sales team at <SalesMail/>.</p>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={close} className="h-10 px-4 rounded-md border border-slate-300 text-sm">Cancel</button>
              <button type="submit" data-testid="int-submit" className="h-10 px-5 rounded-md bg-slate-900 text-white text-sm font-semibold">{interest.kind === "WAITLIST" ? "Reserve my spot" : "Talk to sales"}</button>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-white rounded-xl max-w-md p-6" data-testid="interest-success">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-teal-700">Received</div>
          <div className="font-display font-bold text-lg mt-1 text-slate-900">Thanks, our team will reach out.</div>
          <p className="text-sm text-slate-600 mt-2">Ref #{result.interest_id}</p>
          <button onClick={close} className="mt-4 h-10 px-4 rounded-md bg-slate-900 text-white text-sm font-semibold">Close</button>
        </div>
      )}
    </div>
  );
  return { handleCta, modal, checkoutMsg };
}

export function CtaButton({ s, onClick, checkoutMsg, size = "sm" }) {
  return (
    <>
      <button onClick={() => onClick(s)} data-testid={`ws-cta-${s.sku}`}
              className={`${size === "lg" ? "h-12 px-6 text-sm" : "h-10 text-xs"} w-full rounded-md bg-slate-900 text-white font-semibold uppercase tracking-widest hover:bg-slate-800 inline-flex items-center justify-center gap-2`}>
        {ctaLabel(s)} <ArrowRight size={12}/>
      </button>
      {checkoutMsg?.sku === s.sku && (
        <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs p-2" data-testid={`ws-checkout-msg-${s.sku}`}>{checkoutMsg.message} Contact sales: <SalesMail/></div>
      )}
    </>
  );
}

export function SolutionCard({ s, onCta, checkoutMsg }) {
  const badge = READINESS_BADGE[s.readiness] || READINESS_BADGE.INACTIVE;
  const isAI = AI_TYPES.has(s.solution_type);
  return (
    <div data-testid={`ws-${s.sku}`} className="rounded-xl bg-white border border-slate-200 shadow-sm p-5 flex flex-col hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{s.provider}{isAI && s.department ? ` · ${s.department}` : ""}</div>
          <div className="font-display font-bold text-lg text-slate-900 mt-0.5">{s.name}</div>
        </div>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border whitespace-nowrap ${badge.cls}`}>{badge.label}</span>
      </div>
      <p className="text-sm text-slate-600 mt-2 flex-1">{s.short}</p>
      {(s.outcomes || []).length > 0 && (
        <ul className="mt-3 space-y-1 text-xs text-slate-700">
          {s.outcomes.slice(0, 3).map(o => <li key={o} className="flex items-start gap-1.5"><span className="text-teal-600 mt-0.5">✓</span>{o}</li>)}
        </ul>
      )}
      <div className="mt-4 text-sm font-mono text-slate-900" data-testid={`ws-price-${s.sku}`}>{priceLine(s)}</div>
      {s.requires_active && <div className="mt-1 text-[11px] text-amber-800">Requires active {s.requires_active === "SALESHUB_ANY" ? "SalesHub AI Pro" : s.requires_active} subscription</div>}
      <div className="mt-4 flex flex-col gap-2">
        <a href={`/workforce/${s.slug || s.sku}`} data-testid={`ws-view-${s.sku}`}
           className="h-9 rounded-md border border-slate-300 text-slate-800 text-xs font-semibold uppercase tracking-widest inline-flex items-center justify-center hover:bg-slate-50">
          {s.solution_type === "AI_DEPARTMENT" ? "View Department" : isAI ? "View Employee" : "View Details"}
        </a>
        <CtaButton s={s} onClick={onCta} checkoutMsg={checkoutMsg}/>
      </div>
    </div>
  );
}

function Field({ label, v, on, testid, type = "text", textarea = false, required = true }) {
  const cls = "mt-1 w-full rounded-md border border-slate-300 px-3 text-sm";
  return (
    <div>
      <label className="text-xs font-semibold text-slate-700">{label}</label>
      {textarea ? <textarea value={v} onChange={e => on(e.target.value)} rows={3} required={required} data-testid={testid} className={`${cls} py-2`}/>
                : <input value={v} onChange={e => on(e.target.value)} required={required} type={type} data-testid={testid} className={`${cls} h-10`}/>}
    </div>
  );
}
